/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment, useRef, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import Button from '../General/Button';
import Alert from './Alert';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import { CommonActions } from '@wajeez-web/stores/common';

interface ModalProps {
  onClose: () => void;
  title: string;
  open: boolean;
  children?: JSX.Element;
  position?: string;
  onSubmit?: () => void;
  withScroll?: boolean;
  height?: 'full' | 'auto' | 'screen' | string;
  width?: 'full' | 'auto' | 'screen' | string;
  loading?: boolean;
  handleAlert?: boolean;
  alertTarget?: 'page' | 'modal';
  fontClassName?: string;
  widthClassName?: string;
  overflowClassName?: string;
  zIndexClassName?: string;
}

const Modal = ({
  open,
  onClose,
  title,
  children,
  onSubmit,
  withScroll,
  loading,
  width,
  height,
  handleAlert = true,
  alertTarget,
  fontClassName,
  widthClassName,
  overflowClassName = 'overflow-y-auto',
  zIndexClassName = 'z-10',
}: ModalProps) => {
  const cancelButtonRef = useRef(null);
  const alert = useCommonSelector((state) => state.common.alert);
  const { dir } = useCommonSelector((state) => state.common.settings);
  const dispatch = useCommonDispatch();
  const escFunction = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, []);

  useEffect(() => {
    if (handleAlert) {
      document.addEventListener('keydown', escFunction, false);
    }

    return () => {
      if (handleAlert) {
        document.removeEventListener('keydown', escFunction, false);
        dispatch(CommonActions.setAlert({ ...alert, for: 'page' }));
      }
    };
  }, [handleAlert]);

  useEffect(() => {
    open &&
      alert.for !== 'modal' &&
      dispatch(CommonActions.setAlert({ ...alert, for: 'modal' }));
  }, [open]);
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className={`fixed top-0 bottom-0 ${zIndexClassName} m-auto inset-0 ${overflowClassName} ${fontClassName}`}
        initialFocus={cancelButtonRef}
        onClose={() => {
          onClose();
        }}
      >
        <div
          dir={dir}
          className="flex  items-end justify-center min-h-modal-screen  px-4 pb-20 text-center sm:block"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={` inline-block max-h-screen relative  align-bottom bg-white ${
                widthClassName
                  ? widthClassName
                  : width + ' sm:w-full'
                  ? 'w-' + width + ' sm:w-full'
                  : 'max-w-2xl w-1/2 sm:w-full'
              }  ${
                height ? 'h-' + height : 'h-auto'
              }  rounded-lg text-left shadow-xl transform transition-all mt-[180px] sm:my-8 sm:align-middle`}
            >
              <div className="bg-white  rounded-lg  px-4 pt-5 pb-0 sm:p-6 sm:pb-4 border-b mb-4">
                <div className="flex  w-full  justify-between items-center">
                  {alert.for === 'modal' && (
                    <div className="mb-4">
                      <Alert target={alertTarget} />
                    </div>
                  )}
                  <div className=" flex items-center first:ms-0 flex-1 min-w-0">
                    <div className="leading-normal text-base font-bold  w-100">
                      <ReturnTranslatedText
                        origintext={title}
                        text={`ModalTitles.${title}`}
                      />
                    </div>
                  </div>
                  <div className=" flex items-center first:ms-0">
                    <div className="flex w-full justify-center items-center">
                      <div className="flex items-center justify-end first:ms-0">
                        <button
                          onClick={() => onClose()}
                          type="button"
                          className="leading-normal inline-flex items-center font-normal outline-none  h-8 text-md  bg-transparent  text-gray-800 hover:text-gray-700  "
                          aria-label="Close"
                        >
                          <XIcon className="h-5 w-5 hover:text-red-500 text-warmGray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`px-4 text-start ${
                  onSubmit && 'mb-4'
                } relative pt-0 z-10 pb-4 sm:p-6 sm:pt-0 sm:pb-4 max-h-max-modal-screen ${
                  withScroll
                    ? 'overflow-y-scroll  scrollbar-thin scrollbar-thumb-gray-400 '
                    : 'flex flex-col'
                }`}
              >
                {children}
              </div>
              {onSubmit && (
                <div className={` px-4 mt-2 pb-4 ltr:right-0 rtl:left-0 `}>
                  {
                    <div className="flex flex-row justify-end  ">
                      <Button
                        loading={loading}
                        title="Apply changes"
                        bg="primary"
                        color="white"
                        onClick={onSubmit}
                        disabled={loading}
                      />
                    </div>
                  }
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
